import React from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {Compass} from 'lucide-react'
import {Button} from '~/src/design-system/Primitives'

/**
 * What an unmatched path renders.
 *
 * STFFLG-3995: the inner <Routes> had no catch-all, so any path that matched nothing rendered
 * `null` INSIDE the layout — nav chrome and an empty white content area. That is the worst
 * possible failure: it looks like a slow page or a broken build, gives a user nothing to act on,
 * and gives whoever debugs it no signal at all. The reported bug was a redirect missing a leading
 * slash, and it took a bug report and a video to find because the app said nothing.
 *
 * Naming the path is the point. It turns "the screen is blank" into "it tried to open
 * /my-signatures/documents", which is the whole diagnosis in one line.
 */
const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  padding: var(--space-16) var(--space-6);
  text-align: center;
  font-family: var(--font-sans);
`

const Title = styled.h2`
  margin: 0;
  font-size: var(--fs-h2);
  font-weight: var(--fw-semibold);
  color: var(--text-primary);
`

const Detail = styled.p`
  margin: 0;
  max-width: 42rem;
  font-size: var(--fs-body);
  line-height: var(--lh-normal);
  color: var(--text-secondary);
`

const Path = styled.code`
  display: inline-block;
  margin-top: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--neutral-100);
  color: var(--text-primary);
  font-family: var(--font-mono);
  font-size: var(--fs-caption);
  word-break: break-all;
`

export default function RouteNotFound() {
  const location = useLocation()
  const navigate = useNavigate()

  return (
    <Wrap data-testid="route-not-found">
      <Compass size={32} color="var(--text-secondary)" aria-hidden="true" />
      <Title>We couldn&apos;t find that page</Title>
      <Detail>
        The link may be out of date, or the page may have moved.
        <Path data-testid="route-not-found-path">{location.pathname}</Path>
      </Detail>
      <Button variant="primary" onClick={() => navigate('/', {replace: true})}>
        Go to your home page
      </Button>
    </Wrap>
  )
}
